<script setup>
import Home from "./HomeComponent.vue";
import api from "../api/api.js";
import store from "../store/store.js";
import CryptoJS from "crypto-js";
import ButtonSubmit from "./ButtonSubmit.vue";
import Footer from "./FooterComponent.vue";
import { flashMessage } from "../functions.js";
import Bowser from "bowser";
</script>

<template>
  <div v-if="isLoggedIn">
    <!-- HomeComponent is handled by router-view in App.vue, not here -->
  </div>
  <div v-else class="login-page">
    <div class="login-main">
    <section class="login-hero">
      <div class="login-hero-top">
        <h1 class="login-hero-title">{{ brandData && brandData.login_hero_text ? brandData.login_hero_text : "Grow Your Leadership Skills" }}</h1>
        <div class="login-hero-tag">
          <span class="login-hero-tag-line"></span>
          <span class="login-hero-tag-text">Curated Excellence</span>
        </div>
      </div>
      <div class="login-hero-ticks" aria-hidden="true">
        <span class="tick tick-1"></span>
        <span class="tick tick-2"></span>
        <span class="tick tick-3"></span>
      </div>
      <div class="login-hero-graphic" aria-hidden="true">
        <div class="stair stair-1"></div>
        <div class="stair stair-2"></div>
        <div class="stair stair-3"></div>
        <div class="stair stair-4"></div>
        <div class="stair stair-5"></div>
      </div>
    </section>

    <section class="login-panel">
      <div class="login-panel-inner">
        <div class="login-heading">
          <img v-if="logoPath" :src="logoPath" alt="Brand Logo" class="login-panel-logo" />
          <h3>Welcome!</h3>
          <p class="login-subtext">Sign in here to grow your leadership skills.</p>
        </div>

        <form @submit.prevent="handleLogin()" method="post">
          <div class="form-group">
            <label for="emailInput" class="field-label">Email Address</label>
            <input
              type="text"
              name="username"
              v-model="username"
              class="form-control"
              placeholder="name@company.com"
              required
              id="emailInput"
            />
          </div>
          <div class="form-group">
            <div class="field-label-row">
              <label for="passwordInput" class="field-label">Password</label>
              <router-link to="/forgot_password_screen" class="forgot-password-link"
                >Forgot Password?</router-link
              >
            </div>
            <div class="password-input-wrap">
              <input
                type="password"
                name="password"
                v-model="password"
                class="form-control"
                placeholder="••••••••"
                required
                id="passwordInput"
                autocomplete="current-password"
              />
              <span class="eye-toggle" @click="showPassword()">
                <i
                  id="loginPasswordEye"
                  :class="['fa', 'eye-icon-color', isPasswordVisible ? 'fa-eye-slash' : 'fa-eye']"
                  aria-hidden="true"
                ></i>
              </span>
            </div>
          </div>
          <div class="form-group button-div">
            <ButtonSubmit
              label="Sign In"
              class="login-submit-btn"
              :style="{
                border: '1px solid transparent',
                boxShadow: 'none',
                padding: '14px 20px',
                fontSize: '16px',
                fontWeight: '700',
                borderRadius: '4px',
                width: '100%',
                backgroundColor: brandData && brandData.main_color2 ? brandData.main_color2 : '#b2c225',
                color: brandData && brandData.button_text_color ? brandData.button_text_color : '#0E5071'
              }"
            />
          </div>
        </form>

        </div>
    </section>
    </div>

    <div class="login-footer-wrap">
      <Footer :brand-data="brandData" />
    </div>
  </div>
</template>

<script> 
export default {
  component: [Home, ButtonSubmit],
  name: "LoginComponent",
  props: {
    brandData: {
      type: Object,
      default: () => ({}),
    },
    logoPath: {
      type: String,
      default: "",
    },
  },
  data: () => ({
    myDomain: document.domain,
    dynamicDomain: "",
    isLoggedIn: false,
    logOutButton: true,
    is2FactorAuthDone: false,
    username: "",
    password: "",
    loginResults: [],
    routeAuthResults: [],
    Roles: {},
    currentRole: [],
    platform: "",
    browser: "",
    deviceCore: "",
    operatingSystem: "",
    ip_address: [],
    user_ip_address: "",
    machine_id: "",
    announcements: "",
    pdfLink: 'https://ts.talentsage.com/TalentSage%20LLC%20Terms%20of%20Service%20updated%20as%20of%202024%20July%2020.pdf',
    hours: 24,
    isPasswordVisible: false
  }),
  methods: {
    
    escapeHtml(str) {
      if (!str) return "";
      return String(str)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    },

    checkLoginCookie() {
      const loginCookie = this.getCookie('loginResults')
      if (loginCookie) {
        this.loginResults = JSON.parse(loginCookie)
      }
    },

    checkAuthenticate() {
      const authCookie = this.getCookie('authenticate')
      if (authCookie) {
        this.isLoggedIn = JSON.parse(authCookie)
      }
    },

    setCookie(name, value, hours) {
      const expires = new Date()
      expires.setTime(expires.getTime() + (hours * 60 * 60 * 1000))
      const encodedValue = encodeURIComponent(value)
      document.cookie = `${name}=${encodedValue};expires=${expires.toUTCString()};path=/;secure;sameSite=Lax`
    },

    
    getCookie(name) {
      const cookies = document.cookie.split(';');
      for (let cookie of cookies) {
        const [cookieName, ...cookieParts] = cookie.split('=').map(c => c.trim());
        const cookieValue = cookieParts.join('='); // Handles `=` in values
        if (cookieName === name) {
          try {
            const decodedValue = decodeURIComponent(cookieValue); // Decode URI-encoded values
            if (decodedValue === null || decodedValue === undefined) {
              console.error('Cookie value is null or undefined');
              return null;
            }
            return decodedValue;
          } catch (e) {
            console.error('Error decoding cookie value:', e);
            return null;
          }
        }
      }
      return null;
    },

    
    async handleLogin() {
      this.$emit("passLoadingStatus", { loading: true, message: "Verifying credentials..." });

      // this.setDeviceCookie();
      // save cookie for the report to access
      this.setCookie('a3b3dd4702b847b440c49c1198d77f85', 'f8beb37fb7fb9f0316f42142ac4382fbe94117b2', 24)

      // Ensure IP is fetched before any userLog calls
      if (!this.user_ip_address) {
        try {
          const axios = require("axios");
          const resIP = await axios.get("https://ipapi.co/json/");
          if (resIP.status === 200) {
            this.user_ip_address = resIP.data.ip;
          }
        } catch (e) {
          console.log("Could not fetch IP on login:", e);
        }
      }
      
      try {
        const res = await api.post("auths", {
          email: this.username.toLowerCase().trim(),
          password: this.password,
        });

        if (res.status === 200) {
          // Check if response indicates invalid credentials
          if (res.data.message === "Invalid credentials") {
            alert("Wrong password, please try again");
            this.userLog(0, res.data.ind_id || null, this.username);
            return null;
          }

          // Store the entire response (already an array from API)
          this.loginResults = res.data;
          this.setCookie('loginResults', JSON.stringify(this.loginResults), this.hours)
          this.$emit("passLoadingStatus", { loading: true, message: "Loading your account..." });
                            
          // old local storage
          // this.loginResults = JSON.parse(localStorage.getItem('loginResults'))
          if(res.data) {
            if (this.loginResults.length <= 0) {
              flashMessage(
                this.$flashMessage,
                "#F47820",
                "#ffffff",
                "Wrong username or password!"
              );
              this.userLog(0, null, this.username);
              return null;
            } else if (
              this.loginResults[0].roles == "" ||
              this.loginResults[0].roles == null
            ) {
              flashMessage(
                this.$flashMessage,
                "#F47820",
                "#ffffff",
                "Invalid user"
              );
              this.userLog(0, this.loginResults[0].ind_id, this.username);
              return null;
            }
            
            // old local storage
            // localStorage.setItem('passLoginUp', JSON.stringify(this.loginResults[0]));
            // localStorage.setItem('passBrandUp', JSON.stringify(this.loginResults[0].brandData));
            // localStorage.setItem('authenticate', JSON.stringify(this.isLoggedIn));

            // Sanitize user data before storing


            this.setCookie('passLoginUp', JSON.stringify(this.loginResults[0]), this.hours)
            this.setCookie('passBrandUp', JSON.stringify(this.loginResults[0].brandData), this.hours)
            this.setCookie('authenticate', JSON.stringify(true), this.hours)
            // Store mask_pii for PII masking middleware
            this.setCookie('mask_pii', this.loginResults[0].mask_pii || '0', this.hours)


            //get announcements
            try {
              const UserAnnouncements = await api.post("/get-announcements-by-gospi-role", {
                org_id: this.loginResults[0].org_id,
                suborg_id: this.loginResults[0].suborgs,
                roles: this.loginResults[0].roles,
                ind_id: this.loginResults[0].ind_id,
                for_email: this.loginResults[0].email
              });
              if (UserAnnouncements.status === 200) {
                console.log('Get announcements from login success!',UserAnnouncements.data)
                this.$emit("passAnnouncementsUp", UserAnnouncements.data);
                // old local storage
                // localStorage.setItem('passAnnouncementsUp', JSON.stringify(UserAnnouncements.data));
                this.setCookie('passAnnouncementsUp', JSON.stringify(UserAnnouncements.data))
              }
            } catch (error) {
              console.log(error);
            }

    
            try {
              const resUserLog = await api.get(
                `getUserLogByIndId/${this.loginResults[0].ind_id}`
              );
              if (resUserLog.status === 200) {
                if (resUserLog.data == "" || !resUserLog.data || !resUserLog || resUserLog.data == null) {
                  console.log('no previous user env log data, will send 2FA', resUserLog)
                  // API did not return any result
                  // Check if 2FA is disabled
                  if (this.loginResults[0].disable_2fa !== 1) {
                      const data2FA2 = {
                        loginDetails: this.loginResults[0],
                        email: this.username,
                        ind_id: this.loginResults[0].ind_id,
                        machine_id: this.machine_id,
                        ip_address: this.user_ip_address,
                        is_logged_in: 1,
                        platform: this.platform,
                        operating_system: this.operatingSystem,
                        browser: this.browser,
                        logical_processors: this.deviceCore,
                      };
                      this.$emit("passUserMachineDetailsUp", data2FA2);
                      this.$emit("passLoginUp", this.loginResults[0]);
                      // Intentionally not emitting passBrandUp here — 2FA screen should
                      // keep the domain-based branding already set in App.vue's mounted()
                      this.$router.push("/two_FA");
                      return;
                  }
                } else {
                  // save IP address
                  const axios = require("axios");
                  try {
                    const resIP = await axios.get("https://ipapi.co/json/");
                    if (resIP.status === 200) {
                      this.user_ip_address = resIP.data.ip;
                      console.log("user ip address is ", this.user_ip_address);
                      this.ip_address = resUserLog.data.ip_address ? resUserLog.data.ip_address.split(",").filter(ip => ip.trim() !== "") : [];
                      console.log("database ip address is ", this.ip_address);
                      // end of save IP address
                      // check if user's ip is in the DB (can add up to 3)
                      if (this.ip_address.includes(this.user_ip_address)) {
                        console.log("Condition 2: The users current IP is saved in the Database");
                      } else {
                        if (this.ip_address.length < 21) {
                          this.ip_address.push(this.user_ip_address);
                          const newArrIP = this.ip_address.join(",");
                          const data2FA = {
                            loginDetails: this.loginResults[0],
                            email: this.username,
                            ind_id: this.loginResults[0].ind_id,
                            machine_id: this.machine_id,
                            ip_address: newArrIP,
                            is_logged_in: 1,
                            platform: this.platform,
                            operating_system: this.operatingSystem,
                            browser: this.browser,
                            logical_processors: this.deviceCore,
                          };
                          console.log(
                            `Added ${this.user_ip_address} to the Database`
                          );
                          this.userLog(0, null);
                          // Check if 2FA is disabled
                          if (this.loginResults[0].disable_2fa !== 1) {
                            this.$emit("passUserMachineDetailsUp", data2FA);
                            this.$emit("passLoginUp", this.loginResults[0]);
                            // Intentionally not emitting passBrandUp — 2FA keeps domain-based branding
                            this.$router.push("/two_FA");
                            return;
                          }
                        } else {
                          console.log(
                            `Added ${this.user_ip_address} to the Database and removed the last IP`
                          );
                          // check if the user's ip match the 3 ips in the database
                          if (this.ip_address.includes(this.user_ip_address)) {
                            console.log(
                              "Condition 2: The users current IP is saved in the Database"
                            );
                          } else if (
                            !this.ip_address.includes(this.user_ip_address)
                          ) {
                            console.log(
                              "Condition 2: The users current IP is not saved in the Database"
                            );
                            // userLog will use authenticated user from token, no need to pass ind_id
                            this.userLog(0);
                            this.ip_address.shift();
                            this.ip_address.push(this.user_ip_address);
                            const newArrIP = this.ip_address.join(",");
                            const data2FA = {
                            loginDetails: this.loginResults[0],
                            email: this.username,
                            ind_id: this.loginResults[0].ind_id,
                            machine_id: this.machine_id,
                            ip_address: newArrIP,
                            is_logged_in: 1,
                            platform: this.platform,
                            operating_system: this.operatingSystem,
                            browser: this.browser,
                            logical_processors: this.deviceCore,
                            };
                            // Check if 2FA is disabled
                            if (this.loginResults[0].disable_2fa !== 1) {
                              this.$emit("passUserMachineDetailsUp", data2FA);
                              this.$emit("passLoginUp", this.loginResults[0]);
                              // Intentionally not emitting passBrandUp — 2FA keeps domain-based branding
                              this.$router.push("/two_FA");
                              return;
                            }
                          }
                          this.ip_address.shift();
                          this.ip_address.push(this.user_ip_address);
                        }
                      }
                      const newArrIP = this.ip_address.join(",");
                      const data2FA = {
                        loginDetails: this.loginResults[0],
                        email: this.username,
                        ind_id: this.loginResults[0].ind_id,
                        machine_id: this.machine_id,
                        ip_address: newArrIP,
                        is_logged_in: 1,
                        platform: this.platform,
                        operating_system: this.operatingSystem,
                        browser: this.browser,
                        logical_processors: this.deviceCore,
                      };

                      // check if the user's ip match the 20 ips in the database
                      if (this.ip_address.includes(this.user_ip_address)) {
                        console.log(
                          "Condition 2: The users current IP is saved in the Database"
                        );
                      } else if (
                        !this.ip_address.includes(this.user_ip_address)
                      ) {
                        console.log(
                          "Condition 2: The users current IP is not saved in the Database"
                        );
                        this.userLog(0, null);
                        // Check if 2FA is disabled
                        if (this.loginResults[0].disable_2fa !== 1) {
                          this.$emit("passUserMachineDetailsUp", data2FA);
                          this.$emit("passLoginUp", this.loginResults[0]);
                          // Intentionally not emitting passBrandUp — 2FA keeps domain-based branding
                          this.$router.push("/two_FA");
                          return;
                        }
                      }
                      // check if user's last login is past 10 days
                      const inputDate = new Date(resUserLog.data.datetime);
                      const currentDate = new Date();
                      // Calculate the difference in milliseconds between the input date and the current date
                      const diffInMs =
                        currentDate.getTime() - inputDate.getTime();
                      // Convert the difference to days
                      const diffInDays = Math.floor(
                        diffInMs / (1000 * 60 * 60 * 24)
                      );
                      // Check if the difference is greater than or equal to 10
                      if (diffInDays >= 10) {
                        console.log(
                          "Condition 3: The input date is at least 10 days in the past."
                        );
                        this.userLog(0, null);
                        // Check if 2FA is disabled
                        if (this.loginResults[0].disable_2fa !== 1) {
                          this.$emit("passUserMachineDetailsUp", data2FA);
                          this.$emit("passLoginUp", this.loginResults[0]);
                          // Intentionally not emitting passBrandUp — 2FA keeps domain-based branding
                          this.$router.push("/two_FA");
                          return;
                        }
                      } else {
                        console.log(
                          "Condition 3: The input date is less than 10 days in the past.",  resUserLog.data
                        );
                      }
                    }
                  } catch (error) {
                    console.log(error);
                  }
                }
                // save last login
                // Last login is now updated automatically during login process
                // No separate call needed
                // try {
                //   const lastLogin = await api.put("/auths/lastlogin", {
                //     email: this.username.toLowerCase().trim(),
                //   });
                //   if (lastLogin.status === 200) {
                //     console.log("Last login updated successesfully");
                //   }
                // } catch (error) {
                //   console.log(error);
                // }
                // give screens to logged in role
                this.Roles = this.loginResults[0].roles;
                try {
                  const result = await api.get("roles");
                  if (result.status === 200) {
                    var rolesData = result.data;
                    var newValue = this.Roles.split(", ");
                    var newRoleName = newValue.map(
                      (r) => rolesData.find((f) => f.role_id == r).role_name
                    );
                    this.currentRole = newRoleName;
                    this.$emit("passLoginUp", this.loginResults[0]);
                    this.$emit("passBrandUp", this.loginResults[0].brandData);
                    this.isLoggedIn = true;
                    // userLog will use authenticated user from token, no need to pass ind_id
                    if (!this.ip_address.includes(this.user_ip_address)) {
                      this.ip_address.push(this.user_ip_address);
                    }
                    this.userLog(1, this.loginResults[0].ind_id, this.loginResults[0].email);
                    this.$router.push("/");
                    this.$emit("authenticate", this.isLoggedIn);
                    store.commit(
                      "addAuthString",
                      this.decrypt(
                        this.loginResults[0].auth_string,
                        this.loginResults[0].seed
                      )
                    );
                    // old local storage
                    // localStorage.setItem('passLoginUp', JSON.stringify(this.loginResults[0]));
                    // localStorage.setItem('passBrandUp', JSON.stringify(this.loginResults[0].brandData));
                    // localStorage.setItem('authenticate', JSON.stringify(this.isLoggedIn));

                    // Sanitize user data before storing
                    const sanitizedLoginData = {
                      ...this.loginResults[0],
                      first_name: this.escapeHtml(this.loginResults[0].first_name),
                      last_name: this.escapeHtml(this.loginResults[0].last_name)
                    };

                    this.setCookie('passLoginUp', JSON.stringify(sanitizedLoginData), this.hours)
                    this.setCookie('passBrandUp', JSON.stringify(this.loginResults[0].brandData), this.hours)
                    this.setCookie('authenticate', JSON.stringify(true), this.hours)
                    // Store mask_pii for PII masking middleware
                    this.setCookie('mask_pii', this.loginResults[0].mask_pii || '0', this.hours)

                    const firstNameSafe = this.escapeHtml(this.loginResults[0].first_name);
                    const rolesSafe = this.escapeHtml(this.currentRole.join(", "));

                    this.$flashMessage.show({
                      html: `<div style="background-color: ${
                        this.loginResults[0].brandData.accent_color1
                          ? this.loginResults[0].brandData.accent_color1
                          : "#F47820"
                      }; width:100%;">  
                        <p style="margin-bottom:0; margin-left:1rem; color: #fff;">Welcome ${firstNameSafe}!</p>
                        <p style="margin-top:0; margin-left:1rem; color: #fff;">Role(s): ${rolesSafe}</p>
                      </div>`,
                      clickable: true,
                    });
                  
                  }
                } catch (error) {
                  console.log(error);
                }
              }
            } catch (error) {
              flashMessage(this.$flashMessage, "#F47820", "#ffffff", error.message);
              return null;
            }
          } else {
            flashMessage(
                this.$flashMessage,
                "#F47820",
                "#ffffff",
                "Wrong username or password!"
              );
              // userLog will use authenticated user from token, no need to pass ind_id
              this.userLog(0);
              return null;
          }
        }
      } catch (error) {
        console.log('res.status first try', error);
        if (error.response && error.response.status === 401) {
          alert("Wrong password, please try again");
          this.userLog(0, null, this.username);
        }
      } finally {
        this.$emit("passLoadingStatus", { loading: false });
      }
    },
    showPassword() {
      var x = document.getElementById("passwordInput");
      if (x.type === "password") {
        x.type = "text";
        this.isPasswordVisible = true;
      } else {
        x.type = "password";
        this.isPasswordVisible = false;
      }
      const icon = document.getElementById("loginPasswordEye");
      if (icon) {
        icon.classList.remove("eye-pop");
        void icon.offsetWidth; // force reflow so the animation can restart
        icon.classList.add("eye-pop");
      }
    },
    decrypt(src, passphrase) {
      const bytes = CryptoJS.AES.decrypt(src, passphrase);
      return bytes.toString(CryptoJS.enc.Utf8);
    },
    async userLog(loggedIn, ind_id = null, email = null) {
        const newArrIP = this.ip_address.join(",");
        try {
        // eslint-disable-next-line
        const _res = await api.post("insertUserLog", {
          machine_id: this.machine_id,
          ip_address: newArrIP,
          is_logged_in: loggedIn,
          platform: this.platform,
          operating_system: this.operatingSystem,
          browser: this.browser,
          logical_processors: this.deviceCore,
          // Pass ind_id and email explicitly for failed login attempts
          ind_id: ind_id,
          attempted_email: email,
          login_attempt_ip: this.user_ip_address || null,
        });
      } catch (error) {
        console.log(error);
      }
    },
  },
  beforeUnmount() {
    if (this._sessionCheckInterval) {
      clearInterval(this._sessionCheckInterval);
    }
  },

  async mounted() {
    this.checkAuthenticate()

    // Check token expiration every 60 seconds
    this._sessionCheckInterval = setInterval(async () => {
      if (this.isLoggedIn) {
        try {
          const loginResults = this.getCookie('loginResults');
          const authenticate = this.getCookie('authenticate');
          
          if (!loginResults || !authenticate) {
            console.log('Cookies missing - triggering session expired event');
            clearInterval(this._sessionCheckInterval);
            window.dispatchEvent(new CustomEvent('session-expired'));
          }
        } catch (error) {
          console.error('Token check error:', error);
        }
      }
    }, 60000);
    
    // old local storage
    // if(JSON.parse(localStorage.getItem('authenticate'))){
    //   let data = JSON.parse(localStorage.getItem('authenticate'))
    //   this.isLoggedIn = data
    // }
    
    if (this.myDomain.includes('uat') || this.myDomain === 'localhost') {
      console.log('you are in UAT or local environment')
    } else {
      await api.get('brands/' + this.myDomain).then((result) => {
        this.dynamicDomain = result.data[0].website_terms_url;
      });
    }

    const browser = Bowser.getParser(window.navigator.userAgent);
    this.platform = browser.getPlatformType();
    this.browser = `${browser.getBrowserName()} V.${browser.getBrowserVersion()}`;
    this.operatingSystem = `${browser.getOSName()} ${browser.getOSVersion()}`;

    const axios = require("axios");
    try {
      const resIP = await axios.get("https://ipapi.co/json/");
      if (resIP.status === 200) {
        this.user_ip_address = resIP.data.ip;
        console.log("user ip address is ", this.user_ip_address);
      }
    } catch (error) {
      console.log(error);
    }

    if (navigator.hardwareConcurrency) {
      this.deviceCore = navigator.hardwareConcurrency + " cores";
    } else {
      this.deviceCore = "N/A";
    }
    // get Machine ID
    // this.getMachineID();
    // console.log("Users machine ID is ", this.machine_id);
  },
};
</script>

<style scoped>
.login-page {
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin: 0;
}
.login-main {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}
@media (min-width: 900px) {
  .login-main {
    flex-direction: row;
  }
}

.login-hero {
  display: none;
  position: relative;
  overflow: hidden;
  background: v-bind('brandData && brandData.accent_color1 ? brandData.accent_color1 : "#F47820"');
  color: #ffffff;
  flex-direction: column;
  justify-content: space-between;
  padding: 48px;
  height: 100%;
  flex-shrink: 0;
}
@media (min-width: 900px) {
  .login-hero {
    display: flex;
    width: 50%;
  }
}
 
.login-hero-title {
  max-width: 420px;
  font-size: 2.25rem;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 24px;
  color: #ffffff;
}
.login-hero-tag {
  display: flex;
  align-items: center;
  gap: 16px;
}
.login-hero-tag-line {
  width: 48px;
  height: 1px;
  background: rgba(255, 255, 255, 0.4);
}
.login-hero-tag-text {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
}
.login-hero-ticks {
  position: absolute;
  bottom: 48px;
  left: 48px;
  display: flex;
  align-items: flex-end;
  gap: 8px;
}
.tick {
  display: block;
  width: 4px;
  background: rgba(255, 255, 255, 0.4);
}
.tick-1 { height: 24px; }
.tick-2 { height: 48px; }
.tick-3 { height: 72px; }
.login-hero-graphic {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 80%;
  height: 40%;
  background: rgba(255, 255, 255, 0.05);
  border-top-left-radius: 40px;
  overflow: hidden;
}
.stair {
  position: absolute;
  bottom: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.1);
  height: 16.6%;
}
.stair-1 { width: 50%; bottom: 0; }
.stair-2 { width: 42%; bottom: 16.6%; }
.stair-3 { width: 34%; bottom: 33.2%; }
.stair-4 { width: 26%; bottom: 49.8%; }
.stair-5 { width: 18%; bottom: 66.4%; }

.login-panel {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  padding: 32px 24px;
  height: 100%;
  overflow-y: auto;
}
.login-panel-inner {
  width: 100%;
  max-width: 420px;
}
.login-heading {
  margin-bottom: 32px;
}
.login-panel-logo {
  display: block;
  max-height: 40px;
  width: auto;
  margin-bottom: 24px;
}
.login-heading h3 {
  font-size: 1.75rem;
  font-weight: 600;
  color: #0b1c30;
  margin: 0 0 8px 0;
}
.login-subtext {
  color: #64748b;
  font-size: 0.95rem;
  margin: 0;
}

input[type="checkbox"] {
  transform: scale(2);
  background-color: initial;
  cursor: default;
  appearance: auto;
  box-sizing: border-box;
  margin: 3px 3px 3px 4px;
  padding: initial;
  border: initial;
}

.form-control {
  height: 56px;
  font-size: 1rem;
}

.form-group input {
  font-weight: 400;
  margin-bottom: 20px;
  font-size: 1rem;
  line-height: 1.4375em;
  letter-spacing: 0.00938em;
  color: rgba(0, 0, 0, 0.87);
  box-sizing: border-box;
  cursor: text;
  display: inline-flex;
  align-items: center;
  width: 100%;
  position: relative;
  border-radius: 4px;
  padding: 16.5px 14px;
  box-shadow: none;
  border: 1px solid #e2e8f0;
  background-color: #f8fafc;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.form-group input::placeholder {
  color: #94a3b8;
  opacity: 1;
}
.form-group input:focus {
  outline: none;
  border: 1.5px solid v-bind('brandData && brandData.main_color1 ? brandData.main_color1 : "#002955"');
  border-radius: 4px;
  box-shadow: none;
}
.form-group {
  position: relative;
}
.field-label-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 4px 12px;
  margin-bottom: 6px;
}
.field-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #434750;
  white-space: nowrap;
  margin-bottom: 6px;
}
.forgot-password-link {
  font-size: 12px;
  font-weight: 600;
  color: #ba1a1a;
  white-space: nowrap;
  text-decoration: none;
}
.forgot-password-link:hover {
  color: #93000a;
}
.password-input-wrap {
  position: relative;
}

.login-footer-wrap :deep(.footer-child) {
  padding: 24px 140px 24px 32px;
  margin: 0;
  height: auto;
  border-top: 1px solid #f1f5f9;
  align-items: center;
  font-size: 12px;
}
@media (max-width: 900px) {
  .login-footer-wrap :deep(.footer-child) {
    padding: 24px 32px;
    justify-content: center;
    text-align: center;
  }
}
.login-footer-wrap :deep(#footer-links) {
  align-items: center;
  gap: 4px;
}
.login-footer-wrap :deep(#footer-links a) {
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 500;
  font-size: 12px;
  color: #94a3b8;
  text-decoration: none;
}
.login-footer-wrap :deep(#footer-links a:hover) {
  color: v-bind('brandData && brandData.main_color1 ? brandData.main_color1 : "#123f75"');
}
.login-footer-wrap :deep(#powered-by a) {
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 500;
  font-size: 11px;
  color: #94a3b8;
  text-decoration: none;
}
.eye-toggle {
  position: absolute;
  top: 0;
  bottom: 20px;
  right: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 22px;
  line-height: 2;
  transition: color 0.2s ease;
}
.eye-icon-color {
  color: v-bind('brandData && brandData.accent_color1 ? brandData.accent_color1 : "#F47820"') !important;
}
.eye-toggle i {
  line-height: 1;
  display: block;
  transform: translateY(1px);
}
.eye-toggle:hover {
  color: #f47820bd;
}
.eye-toggle:active {
  color: #ff6a00;
}
.eye-pop {
  animation: eyePop 0.3s ease;
}
@keyframes eyePop {
  0% { transform: scale(0.7); }
  60% { transform: scale(1.25); }
  100% { transform: scale(1); }
}
.button-div {
  display: flex;
  justify-content: center;
  margin-top: 8px;
}
.button-div :deep(.btn-submit) {
  border-radius: 4px !important;
  font-weight: 700 !important;
  width: 100% !important;
}
.flash-message,
.flash-message__wrapper,
[class*="flash-message"] {
  z-index: 10000 !important;
  position: fixed !important;
  top: 0 !important;
}
@media (max-width: 480px) {
  .fa-eye {
    margin-left: -15px;
    cursor: pointer;
    position: relative;
    z-index: 2;
  }
}
</style>
